import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BottomTab from "../../components/common/bottom-tab";
import Header from "../../components/common/header";
import { useLocation } from "../../core/location-provider";
import ScrollRestoreV2 from "./components/scroll-restore-v2";
import { useElementViewCountTracking } from "../home/hooks/use-element-view-count-tracking";
import AppDownloadBottomSheet from "../../components/app-download/app-download-bottom-sheet";
import { isWeb } from "../../utils/user-agent";
import { useInfiniteScrollReviews, type FetchMoreFn } from "./hooks/use-infinite-scroll-reviews";
import { useExploreQuery } from "./hooks/use-explore-query";
import { useExploreFilter } from "./hooks/use-explore-filter";
import { useExploreReviewState } from "./hooks/use-explore-review-state";
import { ExploreFilterChips } from "./components/explore-filter-chips";
import { ExploreReviewListSkeleton } from "./components/explore-review-list-skeleton";
import { ExploreEmptyState } from "./components/explore-empty-state";
import { ExploreReviewList } from "./components/explore-review-list";

const LIMIT = 10;

const Explore = () => {
  const navigate = useNavigate();
  const { hasLastLo, getLocationFromStorage } = useLocation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const fetchMoreRef = useRef<FetchMoreFn | null>(null);

  const { attachObserver } = useElementViewCountTracking({
    threshold: 5,
    onTrigger: () => setIsSheetOpen(true),
    intersectionThreshold: 1,
    enabled: isWeb(),
    enableSessionPersistence: true,
    sessionStorageKey: "explore-download-sheet-shown",
  });

  const { selectedFilter, handleFilter } = useExploreFilter(hasLastLo);

  const {
    rootRef,
    loadMoreRef,
    allReviews,
    setAllReviews,
    hasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
    restoredData,
  } = useInfiniteScrollReviews({
    limit: LIMIT,
    hasLastLo,
    getLocationFromStorage,
    fetchMoreRef,
    selectedFilter,
  });

  const { refetch, fetchMore } = useExploreQuery({
    selectedFilter,
    hasLastLo,
    getLocationFromStorage,
    restoredData,
    setAllReviews,
    setInitLoading,
    limit: LIMIT,
  });

  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  const onFilterChange = useCallback(
    (filter: "all" | "nearby") => {
      if (!handleFilter(filter)) return;
      setAllReviews([]);
      setInitLoading(true);
      refetch();
    },
    [handleFilter, setAllReviews, setInitLoading, refetch]
  );

  const { expandedReviews, imageLoaded, toggleReview, handleImageLoad } =
    useExploreReviewState();

  return (
    <div className="w-full h-screen flex flex-col">
      <Header title="찐후기" />

      <ScrollRestoreV2
        ref={rootRef}
        id="scroll-explore"
        initRestore={!initLoading}
        className="overflow-y-auto flex-1 pb-16 h-full"
        showScrollTopButton
      >
        <ExploreFilterChips
          selectedFilter={selectedFilter}
          onFilterChange={onFilterChange}
          initLoading={initLoading}
        />
        {initLoading && <ExploreReviewListSkeleton />}
        {!initLoading && allReviews.length > 0 && (
          <ExploreReviewList
            allReviews={allReviews}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            expandedReviews={expandedReviews}
            imageLoaded={imageLoaded}
            onToggleReview={toggleReview}
            onImageLoad={handleImageLoad}
            rootRef={rootRef}
            loadMoreRef={loadMoreRef}
            attachObserver={attachObserver}
          />
        )}
        {!initLoading && allReviews.length === 0 && (
          <ExploreEmptyState onNavigateHome={() => navigate("/")} />
        )}
      </ScrollRestoreV2>

      <AppDownloadBottomSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
      />

      <BottomTab />
    </div>
  );
};

export default Explore;
